import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Svg } from "../../../../components/__common/Svg/Svg";
import { Typography } from "../../../../components/__common/Typography/Typography";
import { Button } from "../../../../components/__common/_controls";
import { MyDataRequestDetail } from "../../../../data/mydata-request-detail-screen";
import { ApplicationState } from "../../../../store";
import { MyDataState } from "../../../../store/reducers/mydata-reducer";
import { RequestStatus, RequestType } from "../../../../types/mydata";
import { RequestDetailsEntity } from "../../../../types/request-details";
import { UtilsDateTime } from "../../../../utils/UtilsDateTIme";
interface Props {
  details: RequestDetailsEntity;
  status: RequestStatus;
  requestType: RequestType;
  isSendPaswwordAPIOngoing: boolean;
  onClickTransferPassword: () => void;
}

export const TitleSection: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const myDataState = useSelector<ApplicationState, MyDataState>(
    (state) => state.myData
  );

  const { isFilePasswordSent } = myDataState;

  const {
    status,
    requestType,
    onClickTransferPassword,
    details,
    isSendPaswwordAPIOngoing,
  } = props;

  const handleOnClickTransferPassword = () => {
    onClickTransferPassword();
  };

  const handleOnClickDownloadAllFiles = () => {
    // Filter all subrequests with attach files
    const subReqWithFile = details.sub_request_list.filter(
      (s) => s.attach_file && s.attach_file.length > 0
    );

    // Get all files from filtered sub-requests and filter out undefined
    const allFiles = subReqWithFile
      .flatMap((subreq) => subreq.attach_file)
      .filter((file): file is NonNullable<typeof file> => !!file);

    // Trigger all downloads in parallel using Promise.all
    Promise.all(
      allFiles.map((file) => {
        return new Promise<void>((resolve) => {
          const element = document.createElement("iframe");
          element.setAttribute("src", file.url);
          element.style.display = "none";
          document.body.appendChild(element);
          resolve();
        });
      })
    );
  };

  const isSomeSubRequestCompleted = details.sub_request_list.some(
    (s) => s.sub_request_status === RequestStatus.COMPLETED
  );

  const isStatusReadyForDownload = [
    RequestStatus.COMPLETED,
    RequestStatus.PROCESSING,
    RequestStatus.QUEUED,
  ].includes(status);

  const isCompletedDownloadRequest =
    isStatusReadyForDownload &&
    isSomeSubRequestCompleted &&
    requestType === RequestType.DOWNLOAD;

  const renderDownloadableContent = () => (
    <>
      <Typography
        variant="title3"
        component="div"
        className="request-details__text-section__title"
      >
        {t(MyDataRequestDetail[status][requestType].title)}
      </Typography>
      <Typography
        variant="body2"
        weight="w400"
        component="div"
        className="request-details__text-section__subtitle"
      >
        {t(MyDataRequestDetail[status][requestType].subtitle)}
      </Typography>
    </>
  );

  const renderDefaultContent = () => (
    <>
      {status === RequestStatus.CANCELED &&
        requestType === RequestType.DELETE && (
          <Typography variant="title3" component="div" weight="w700">
            {t("PRIVACY_REQUEST_DETAILS_CANCELED_DELETE")}
          </Typography>
        )}

      {status === RequestStatus.CANCELED &&
        requestType === RequestType.DOWNLOAD && (
          <Typography variant="title3" component="div" weight="w700">
            {t("PRIVACY_REQUEST_DETAILS_CANCELED")}
          </Typography>
        )}

      {status === RequestStatus.DENIED && (
        <Typography variant="title3" component="div" weight="w700">
          {t("PRIVACY_MYDATA_REQ_ERROR_TITLE")}
        </Typography>
      )}

      <Typography
        variant="body2"
        weight="w400"
        component="div"
        className="request-details__text-section__subtitle"
      >
        {t(MyDataRequestDetail[status][requestType].subtitle)}
      </Typography>
    </>
  );

  const renderTitleContent = () => {
    if (isCompletedDownloadRequest) {
      return renderDownloadableContent();
    }
    return renderDefaultContent();
  };

  return (
    <>
      <div className="request-details__icon-main">
        <Svg
          icon={MyDataRequestDetail[status][requestType].icon}
          width={96}
          height={96}
        />
      </div>
      <div className="request-details__text-section">
        <Typography
          variant="body2"
          component="div"
          className="request-details__text-section__status"
        >
          {t(MyDataRequestDetail[status][requestType].statusLabel)}
        </Typography>
        {renderTitleContent()}
      </div>
      <div className="request-details__button">
        {isCompletedDownloadRequest &&
          requestType === RequestType.DOWNLOAD &&
          UtilsDateTime.getExpiredDate(details) &&
          !isFilePasswordSent && (
            <div className="file-button-container">
              <span>
                <Button
                  testId="transfer-password-button"
                  variant="solid"
                  type="secondary"
                  title={t(
                    "PRIVACY_MYDATA_REQ_DETAIL_BUTTON_SEND_FILE_PASSWORD"
                  )}
                  onClick={handleOnClickTransferPassword}
                  disabled={isSendPaswwordAPIOngoing}
                />
              </span>
              <span>
                <Button
                  testId="download-all-file-button"
                  variant="solid"
                  type="secondary"
                  title={t("PRIVACY_REQUEST_DETAILS_BTN_DOWNLOAD_ALL")}
                  onClick={handleOnClickDownloadAllFiles}
                />
              </span>
            </div>
          )}
      </div>
    </>
  );
};
